<script setup lang="ts">
/*
 *
 * IMPORTS
 *
 * */

// PRIMEVUE
import { default as PInputGroup } from 'primevue/inputgroup'
import { default as PTextarea } from 'primevue/textarea'
import { default as PRadioButton } from 'primevue/radiobutton'
import { default as PInputGroupAddon } from 'primevue/inputgroupaddon'
import { default as PButton } from 'primevue/button'

// Custom components and types
import { QuizQuestionAnswerSingleChoice } from '@/core/domain/quiz/QuizQuestionAnswer.ts'
import { LearningQuizQuestionType } from '@/core/domain/resources/enums/LearningQuizQuestion.ts'

// VUE, VUE ROUTER, ETC...
import { type ModelRef, onMounted, type Ref, ref, watch } from 'vue'

const answers: ModelRef<QuizQuestionAnswerSingleChoice[]> = defineModel('answers', {
  required: true
})

const props = defineProps<{
  canModifyStructure?: boolean
  canModifyContent?: boolean
}>()

const correctAnswer: Ref<number | null> = ref(null)

// Fonction utilitaire pour synchroniser la réponse correcte
function syncCorrectAnswer() {
  correctAnswer.value = null
  answers.value.forEach((answer, i) => {
    if (answer.isCorrect) {
      correctAnswer.value = i
    }
  })
}

onMounted(() => {
  syncCorrectAnswer()
})

function addAnswer() {
  answers.value.push(
    QuizQuestionAnswerSingleChoice.create({
      label: '',
      isCorrect: false,
      type: LearningQuizQuestionType.Values.SINGLE_CHOICE
    })
  )
}

function updateAnswers() {
  if (correctAnswer.value !== null) {
    answers.value.forEach((answer, i) => {
      answer.isCorrect = i === correctAnswer.value
    })
  }
}

function deleteAnswer(index: number) {
  answers.value.splice(index, 1)
  // Mettre à jour correctAnswer après suppression
  syncCorrectAnswer()
}

// Observer les changements dans answers
watch(answers, () => {
  syncCorrectAnswer()
})
</script>

<template>
  <div class="flex flex-col gap-4">
    <p-input-group v-for="(answer, i) in answers" :key="`answer_${i}`" class="w-full">
      <p-input-group-addon>
        <p-radio-button
          :value="i"
          v-model="correctAnswer"
          name="answer"
          :disabled="!props.canModifyContent"
          @update:model-value="() => updateAnswers()"
        />
      </p-input-group-addon>
      <p-textarea
        v-model="answer.label"
        placeholder="Texte de la réponse"
        class="flex-1"
        :disabled="!props.canModifyContent"
      />
      <p-input-group-addon>
        <p-button
          @click="deleteAnswer(i)"
          icon="pi pi-trash"
          text
          severity="danger"
          :disabled="!props.canModifyStructure"
        />
      </p-input-group-addon>
    </p-input-group>

    <!-- Message d'information si pas de modifications structurelles autorisées -->
    <div v-if="!props.canModifyStructure && props.canModifyContent" class="text-xs text-amber-600">
      <i class="pi pi-info-circle mr-1"></i>
      Impossible d'ajouter/supprimer des réponses sur cette version
    </div>

    <div v-if="!props.canModifyContent" class="text-xs text-red-600">
      <i class="pi pi-lock mr-1"></i>
      Version verrouillée - aucune modification autorisée
    </div>
  </div>

  <p-button
    label="Ajouter une réponse"
    icon="pi pi-plus"
    class="mt-4"
    size="small"
    outlined
    :disabled="!props.canModifyStructure"
    @click="addAnswer"
  />
</template>
